import React, { SyntheticEvent, useEffect, useState } from "react";
import {
  Modal,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/system/Grid";
import { Controller, useForm } from "react-hook-form";

import { flexAllEnd } from "../../../styles/mui/styles/display";
import {
  ModalContainer,
  CountTextfield,
  CheckboxGrid,
  DataGrid,
  NtuReasonAutocomplete,
  StyledTextarea,
  CheckboxStyle,
  PatientName,
  VisitGrid,
} from "../style/style";
import fontWeight from "../../../styles/mui/fontWeight";
import { colors } from "../../../styles/colors";
import { ButtonCloseStyle, ButtonSureStyle, ModalOuterContainer } from "../../../app/styles/styles";
import { NtuPatientPayload, ReferralDetail } from "../../../models/Referral";
import { DateFormats } from "../../../constants/Date";
import { formatDate } from "../../../utils/DateTime";
import { length, getValue, handleInputEvent, removeAccents } from "../../../utils";
import { rules } from "../../../utils/validation/Validation";
import { TextfieldLabel } from "../../../app/common/TextLabel";
import { ListOptions } from "../../../models/Master";
import { matchSorter } from "match-sorter";
import { NO_OPTIONS } from "../../../constants/ResponseMessage";
import useMenuItem from "../../../hooks/useMenuItem";
import { defaultListData } from "../../../constants/HomePage";

export interface PropsFromState {
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  referralInfo: ReferralDetail;
  masterNtuReasonState: ListOptions[];
}

export interface PropsFromDispatch {
  postNtuPatient: (payload: NtuPatientPayload) => void;
  getMasterNtuReason: (typeId: boolean) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const NtuPatient: React.FC<AllProps> = ({
  isOpenModal,
  setIsOpenModal,
  referralInfo,
  masterNtuReasonState,
  getMasterNtuReason,
  postNtuPatient,
}) => {
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const { open, onOpen, onClose } = useMenuItem();
  const [ntuReason, setNtuReason] = useState<ListOptions>(defaultListData);

  useEffect(() => {
    if (!length(masterNtuReasonState)) {
      getMasterNtuReason(true);
    }
  }, []);

  const methods = useForm<NtuPatientPayload>({
    defaultValues: {
      completedVisits: "0",
      description: "",
      dob: formatDate(referralInfo.dateOfBirth, DateFormats.MM_DD_YYYY),
      ntuReason: "",
      patientName: `${referralInfo.firstName} ${referralInfo.lastName}`,
      referralId: getValue(referralInfo, "referralId", 0).toString(),
      restaff: "No",
    },
  });

  const { handleSubmit, control, setValue, clearErrors } = methods;

  const handleClose = () => {
    setIsOpenModal(false);
  };

  const handleSendNtu = () => {
    setIsConfirmationVisible(true);
  };

  const onSubmit = (data: NtuPatientPayload) => {
    postNtuPatient(data);
    setIsOpenModal(false);
  };

  const handleConfirm = () => {
    handleSubmit(onSubmit)();
  };

  const handleNtuReasonChange = (
    _e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    onClose();
    setNtuReason(value);
    setValue("ntuReason", value.label);
    clearErrors("ntuReason");
  };

  return (
    <Modal
      open={isOpenModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={ModalOuterContainer}
    >
      <Grid container sx={ModalContainer}>
        <Grid size={{ xs: 12 }} pt={1} pb={4}>
          <Typography
            variant="h1"
            fontWeight={fontWeight[3]}
            color={colors.portalColor[2]}
            sx={PatientName}
          >
            {referralInfo.firstName}&nbsp;{referralInfo.lastName}
          </Typography>
          <Typography
            variant="h3"
            fontWeight={fontWeight[5]}
            color={colors.portalColor[2]}
            pt={"0.9rem"}
          >
            DOB:&nbsp;
            {formatDate(referralInfo.dateOfBirth, DateFormats.MM_DD_YYYY)}
          </Typography>
          <Typography
            variant="h3"
            fontWeight={fontWeight[4]}
            color={colors.portalColor[2]}
            pt={"0.9rem"}
          >
            Auth#:&nbsp;
            {referralInfo.authorizationNumber}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }} sx={DataGrid}>
          {TextfieldLabel("NTU Reason", true)}
          <Controller
            name="ntuReason"
            control={control}
            rules={rules.ntuReason}
            render={({ field, fieldState: { error } }) => (
              <>
                <Autocomplete //tbd
                  sx={NtuReasonAutocomplete}
                  options={masterNtuReasonState}
                  freeSolo
                  forcePopupIcon
                  getOptionLabel={(option: any) => option.label}
                  open={open}
                  onOpen={() => onOpen()}
                  onClose={() => onClose()}
                  filterOptions={(options: any, { inputValue }) => {
                    const normalizedInput = removeAccents(inputValue);
                    return matchSorter(options, normalizedInput, { keys: ["label"] });
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  id="auto-complete"
                  autoComplete
                  disableClearable
                  clearIcon={""}
                  onChange={handleNtuReasonChange}
                  includeInputInList
                  value={ntuReason}
                  noOptionsText={NO_OPTIONS}
                  renderInput={(params: any) => {
                    return (
                      <TextField
                        {...params}
                        {...field}
                        variant="outlined"
                        placeholder="Select Reason"
                        InputProps={{
                          ...params.InputProps,
                          type: "search",
                        }}
                      />
                    );
                  }}
                  renderOption={(props, option) => {
                    return (
                      <Typography
                        {...props}
                        variant="body1"
                        fontWeight={fontWeight[4]}
                        color={colors.fonts[25]}
                        key={option.value}
                      >
                        {option.label}
                      </Typography>
                    );
                  }}
                />
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={DataGrid}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <StyledTextarea
                {...field}
                minRows={3}
                placeholder="Description"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={VisitGrid}>
          {TextfieldLabel("How many total visits were completed?", true)}
          <Controller
            name="completedVisits"
            control={control}
            rules={rules.completedVisits}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  id="outlined-basic"
                  variant="outlined"
                  sx={CountTextfield}
                  onInput={(e) => {
                    handleInputEvent(e, "onlyNumber");
                  }}
                  size="small"
                />
                {error?.message && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={CheckboxGrid}>
          <Controller
            name="restaff"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                label={
                  <Typography
                    variant="h2"
                    fontWeight={fontWeight[2]}
                    color={colors.portalColor[2]}
                  >
                    Re-Staff this Patient?
                  </Typography>
                }
                control={
                  <Checkbox
                    {...field}
                    checked={field.value === "Yes"}
                    size="small"
                    sx={CheckboxStyle}
                    onChange={(event) => {
                      field.onChange(event.target.checked ? "Yes" : "No");
                    }}
                  />
                }
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={flexAllEnd}>
          {isConfirmationVisible ? (
            <Button sx={ButtonSureStyle} onClick={handleConfirm}>
              Are you sure?
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSendNtu}>
              Send NTU
            </Button>
          )}
          <Button sx={ButtonCloseStyle} onClick={handleClose}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default NtuPatient;
